import { useEffect, useRef, useState } from "react";
import { Form } from "antd";
import Modal from "@/common/elements/Modal";
import Button from "@/common/elements/Button";
import TextField from "@/common/elements/InputElements/TextField";
import Select from "@/common/elements/InputElements/Select";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import DataTable from "@/common/elements/DataTable";
import { StyledGridCol } from "@/common/styles";
import { labels } from "@/util/label";
import notificationService from "@/util/notificationService";
import {
  isNonEmptyVariable,
  getOptionsFromRefList,
} from "@/util/common_function";
import { searchFolders } from "@/pages/FolderSearch/folderSearchApi";
import { searchBookingsModule, searchWarrantsModule } from "./moduleSearchApi";
import JacketFullSummaryReport from "./JacketFullSummaryReport";
import BookingSummaryReport from "./BookingSummaryReport";
import WarrantSummaryReport from "./WarrantSummaryReport";
import { SearchFormRow, ResultsHint } from "./styles";

// Matches the field set on the legacy ASP "Super Search" dialog's Jackets
// tab (InvestigatorsViewInterface.asp): Last/First Name/Search Type row,
// a module-dependent bio row, and the Module row. Selecting a Module
// switches which API gets hit (and which extra fields apply), same as
// "if I choose Bookings, call the bookings API; Warrants, the warrants
// API" — verified against the actual Spring Boot services (read-only, no
// Java touched):
//   - "" (blank)  -> POST bookings/jackets/search (searchFolders, shared
//                    with the Folder Search page)
//   - "Bookings"  -> POST bookings/search (searchBookingsModule)
//   - "Warrants"  -> POST warrant/search (searchWarrantsModule)
//   - "Civil Papers" -> disabled; no search API exists anywhere in this
//                    app for it yet (checked api.js and the CivilPapers
//                    page) — nothing to call.
//
// Search Type is a real, functioning filter for Bookings and Warrants
// (both services branch on STARTS_WITH/EXACT/FUZZY/SOUNDS_LIKE — Warrants
// even requires it, throwing if missing, which is why it's always sent).
// It's still a no-op for the blank/Jackets case — JacketsService.jacketsSearch
// doesn't branch on it.
//
// Warrant search's bio fields are named differently server-side
// (lastName/firstName/middleName, no race/sex/dlNumber support at all) —
// so the field set swaps rather than being reused as-is.
const MODULE_OPTIONS = [
  { value: "Bookings", label: "Bookings" },
  { value: "Warrants", label: "Warrants" },
  {
    value: "Civil Papers",
    label: "Civil Papers",
    disabled: true,
    title: "Not available yet — no search API exists for Civil Papers.",
  },
];

const JACKET_HEADERS = [
  { key: "jacketId", text: labels.jacketID },
  { key: "lName", text: labels.lastName },
  { key: "fName", text: labels.firstName },
  { key: "mName", text: labels.middleName },
  { key: "dob", text: labels.dob },
  { key: "race", text: labels.race },
  { key: "sex", text: labels.gender },
];

const BOOKING_HEADERS = [
  { key: "jacketId", text: labels.jacketID },
  { key: "name", text: "Full Name" },
  { key: "dob", text: labels.dob },
  { key: "incarcerationDate", text: "Incarceration Date" },
  { key: "releaseDate", text: "Release Date" },
  { key: "housingAgency", text: "Housing Agency" },
  { key: "housingLocation", text: "Current Location" },
];

const WARRANT_HEADERS = [
  { key: "jacketId", text: labels.jacketID },
  { key: "lName", text: labels.lastName },
  { key: "fName", text: labels.firstName },
  { key: "mName", text: labels.middleName },
  { key: "dob", text: labels.dob },
  { key: "status", text: "Status" },
];

// Keyed by the Module field's value ("" = Jackets/blank).
const MODULE_META = {
  "": {
    headers: JACKET_HEADERS,
    rowUniqueId: "jacketId",
    plural: "jackets",
    searchFn: searchFolders,
    SummaryComponent: JacketFullSummaryReport,
  },
  Bookings: {
    headers: BOOKING_HEADERS,
    rowUniqueId: "jacketId",
    plural: "bookings",
    searchFn: searchBookingsModule,
    SummaryComponent: BookingSummaryReport,
  },
  Warrants: {
    headers: WARRANT_HEADERS,
    rowUniqueId: "jacketId",
    plural: "warrants",
    searchFn: searchWarrantsModule,
    SummaryComponent: WarrantSummaryReport,
  },
};

const DEFAULT_PAGINATION = { pageIndex: 0, pageSize: 25 };

// Alt+S "Super Search" — the React-app equivalent of the legacy ASP app's
// InvestigatorsViewStandalone.asp?Event=SuperSearch popup: search by name
// (optionally scoped to a Module), then click a result to view its
// read-only summary.
//
// Mounted once at the App shell level and toggled by useSuperSearchShortcut,
// so it's reachable from anywhere in the app without losing the page you
// were on underneath it.
const SuperSearchModal = ({ show, onHide }) => {
  const [form] = Form.useForm();
  const moduleValue = Form.useWatch("module", form) || "";

  const [step, setStep] = useState("search"); // "search" | "summary"
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({ total: 0, rows: [] });
  const [selectedRow, setSelectedRow] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [hasSearched, setHasSearched] = useState(false);
  // The module the *last executed* search ran under — drives which result
  // columns/summary render. Kept separate from the live `moduleValue` watch
  // so changing the dropdown after a search doesn't relabel results you
  // haven't re-searched for yet.
  const [activeModule, setActiveModule] = useState("");

  // Guards the pagination-change effect so it only re-fetches once a search
  // has actually been run (mirrors FolderSearch's effectRan.current pattern)
  // — otherwise it would fire on mount/reset with an empty name filter.
  const searchedRef = useRef(false);

  const resetState = () => {
    form.resetFields();
    setStep("search");
    setResults({ total: 0, rows: [] });
    setSelectedRow(null);
    setPagination(DEFAULT_PAGINATION);
    setHasSearched(false);
    setActiveModule("");
    searchedRef.current = false;
  };

  const handleHide = () => {
    resetState();
    onHide?.();
  };

  const buildFilter = (values) => {
    const module = values?.module || "";
    const lName = values?.lName?.trim();
    const fName = values?.fName?.trim();
    const filter = [];

    if (isNonEmptyVariable(values?.searchType))
      filter.push({ filterKey: "searchType", value: values.searchType });

    if (module === "Warrants") {
      if (isNonEmptyVariable(lName))
        filter.push({ filterKey: "lastName", value: lName });
      if (isNonEmptyVariable(fName))
        filter.push({ filterKey: "firstName", value: fName });
      if (isNonEmptyVariable(values?.mName?.trim?.()))
        filter.push({ filterKey: "middleName", value: values.mName.trim() });
      if (isNonEmptyVariable(values?.dob?.trim?.()))
        filter.push({ filterKey: "dob", value: values.dob.trim() });
      if (isNonEmptyVariable(values?.ssn?.trim?.()))
        filter.push({ filterKey: "ssn", value: values.ssn.trim() });
      if (isNonEmptyVariable(values?.sid?.trim?.()))
        filter.push({ filterKey: "sid", value: values.sid.trim() });
      if (isNonEmptyVariable(values?.warrantNumber?.trim?.()))
        filter.push({
          filterKey: "warrantNumber",
          value: values.warrantNumber.trim(),
        });
      if (isNonEmptyVariable(values?.courtNumber?.trim?.()))
        filter.push({
          filterKey: "courtNumber",
          value: values.courtNumber.trim(),
        });
      if (isNonEmptyVariable(values?.crnNo?.trim?.()))
        filter.push({ filterKey: "crnNo", value: values.crnNo.trim() });
      if (isNonEmptyVariable(values?.statusID))
        filter.push({ filterKey: "statusID", value: values.statusID });
    } else {
      // lName/fName are server-side "starts with" for Jackets; race/sex/
      // dob/ssn/dlNumber/sid are exact matches — all confirmed supported
      // by both bookings/jackets/search and bookings/search.
      if (isNonEmptyVariable(lName))
        filter.push({ filterKey: "lName", value: lName });
      if (isNonEmptyVariable(fName))
        filter.push({ filterKey: "fName", value: fName });
      if (isNonEmptyVariable(values?.race))
        filter.push({ filterKey: "race", value: values.race });
      if (isNonEmptyVariable(values?.sex))
        filter.push({ filterKey: "sex", value: values.sex });
      if (isNonEmptyVariable(values?.dob?.trim?.()))
        filter.push({ filterKey: "dob", value: values.dob.trim() });
      if (isNonEmptyVariable(values?.ssn?.trim?.()))
        filter.push({ filterKey: "ssn", value: values.ssn.trim() });
      if (isNonEmptyVariable(values?.dlNumber?.trim?.()))
        filter.push({ filterKey: "dlNumber", value: values.dlNumber.trim() });
      if (isNonEmptyVariable(values?.sid?.trim?.()))
        filter.push({ filterKey: "sid", value: values.sid.trim() });

      if (module === "Bookings") {
        if (isNonEmptyVariable(values?.incarcerationDate?.trim?.()))
          filter.push({
            filterKey: "incarcerationDate",
            value: values.incarcerationDate.trim(),
          });
        if (isNonEmptyVariable(values?.releaseDate?.trim?.()))
          filter.push({
            filterKey: "releaseDate",
            value: values.releaseDate.trim(),
          });
        if (isNonEmptyVariable(values?.bookingNumber?.trim?.()))
          filter.push({
            filterKey: "bookingNumber",
            value: values.bookingNumber.trim(),
          });
      }
    }

    return filter;
  };

  const runSearch = (paginationValue) => {
    const values = form.getFieldsValue();
    const module = values?.module || "";
    const lName = values?.lName?.trim();
    const fName = values?.fName?.trim();

    if (!isNonEmptyVariable(lName) && !isNonEmptyVariable(fName)) {
      return;
    }

    const filter = buildFilter(values);
    const searchFn = MODULE_META[module]?.searchFn || searchFolders;

    setIsLoading(true);
    searchFn({ requestFilter: filter, pagination: paginationValue })
      .then((data) => {
        setResults(data);
        setHasSearched(true);
        setActiveModule(module);
      })
      .catch(() => {
        notificationService.error(
          "Search Failed",
          "Search failed. Please try again.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  // Re-run the search whenever pagination changes (page or page size),
  // exactly like FolderSearch does — DataTable drives `pagination` both via
  // plain-object updates and via functional (prev => next) updates, so it
  // needs the raw setState setter, not a wrapper we intercept synchronously.
  useEffect(() => {
    if (searchedRef.current) {
      runSearch(pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  const handleSearch = () => {
    form
      .validateFields()
      .then((values) => {
        const lName = values?.lName?.trim();
        const fName = values?.fName?.trim();
        if (!isNonEmptyVariable(lName) && !isNonEmptyVariable(fName)) {
          notificationService.error(
            "Validation Error",
            "Please enter at least a Last Name or First Name to search.",
          );
          return;
        }
        searchedRef.current = true;
        if (pagination.pageIndex === 0) {
          runSearch(pagination);
        } else {
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }
      })
      .catch(() => {
        // antd already highlights invalid fields
      });
  };

  const handleClearForm = () => {
    form.resetFields();
    setResults({ total: 0, rows: [] });
    setHasSearched(false);
    setActiveModule("");
    searchedRef.current = false;
  };

  const handleRowClick = (row) => {
    if (!row) return;
    setSelectedRow(row);
    setStep("summary");
  };

  const handleBackToResults = () => {
    setSelectedRow(null);
    setStep("search");
  };

  const activeMeta = MODULE_META[activeModule] || MODULE_META[""];
  const SummaryComponent = activeMeta.SummaryComponent;

  return (
    <Modal
      header={step === "summary" ? "Summary" : "Super Search — Jacket Lookup"}
      show={show}
      size="xxl"
      onHide={handleHide}
      handleCancel={handleHide}
      footerActionsNeed={false}
    >
      {step === "search" && (
        <>
          <Form
            form={form}
            onFinish={handleSearch}
            initialValues={{ searchType: "Starts With" }}
          >
            <SearchFormRow gutter={16} align="bottom">
              <StyledGridCol span={8}>
                <FormItem name="lName" label={labels.lastName}>
                  <TextField id="SuperSearchLastName" autoFocus />
                </FormItem>
              </StyledGridCol>
              <StyledGridCol span={8}>
                <FormItem name="fName" label={labels.firstName}>
                  <TextField id="SuperSearchFirstName" />
                </FormItem>
              </StyledGridCol>
              <StyledGridCol span={8}>
                <FormItem name="searchType" label="Search Type">
                  <Select options={getOptionsFromRefList("Search_Type")} />
                </FormItem>
              </StyledGridCol>
            </SearchFormRow>

            {moduleValue === "Warrants" ? (
              <>
                <SearchFormRow gutter={16} align="bottom">
                  <StyledGridCol span={6}>
                    <FormItem name="mName" label={labels.middleName}>
                      <TextField id="SuperSearchMiddleName" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={6}>
                    <FormItem name="dob" label={labels.dob}>
                      <TextField id="SuperSearchDOB" placeholder="MM/DD/YYYY" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={6}>
                    <FormItem name="ssn" label={labels.ssn}>
                      <TextField id="SuperSearchSSN" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={6}>
                    <FormItem name="sid" label={labels.sid}>
                      <TextField id="SuperSearchSID" />
                    </FormItem>
                  </StyledGridCol>
                </SearchFormRow>

                <SearchFormRow gutter={16} align="bottom">
                  <StyledGridCol span={6}>
                    <FormItem name="warrantNumber" label="Warrant Number">
                      <TextField id="SuperSearchWarrantNumber" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={6}>
                    <FormItem name="courtNumber" label="Court Number">
                      <TextField id="SuperSearchCourtNumber" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={6}>
                    <FormItem name="crnNo" label="Case Number">
                      <TextField id="SuperSearchCaseNumber" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={6}>
                    <FormItem name="statusID" label="Status">
                      <Select
                        options={getOptionsFromRefList(
                          "Status",
                          false,
                          false,
                          true,
                        )}
                        placeholder="Status"
                        allowClear
                      />
                    </FormItem>
                  </StyledGridCol>
                </SearchFormRow>
              </>
            ) : (
              <>
                <SearchFormRow gutter={16} align="bottom">
                  <StyledGridCol span={4}>
                    <FormItem name="race" label={labels.race}>
                      <Select
                        options={getOptionsFromRefList("Race")}
                        placeholder="Race"
                        allowClear
                      />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <FormItem name="sex" label={labels.gender}>
                      <Select
                        options={getOptionsFromRefList("gender", false, true)}
                        placeholder="Sex"
                        allowClear
                      />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <FormItem name="dob" label={labels.dob}>
                      <TextField id="SuperSearchDOB" placeholder="MM/DD/YYYY" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <FormItem name="ssn" label={labels.ssn}>
                      <TextField id="SuperSearchSSN" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <FormItem name="dlNumber" label={labels.dlNumber}>
                      <TextField id="SuperSearchDLNumber" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={4}>
                    <FormItem name="sid" label={labels.sid}>
                      <TextField id="SuperSearchSID" />
                    </FormItem>
                  </StyledGridCol>
                </SearchFormRow>

                {moduleValue === "Bookings" && (
                  <SearchFormRow gutter={16} align="bottom">
                    <StyledGridCol span={8}>
                      <FormItem name="incarcerationDate" label="Booked Date">
                        <TextField
                          id="SuperSearchBookedDate"
                          placeholder="MM/DD/YYYY"
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={8}>
                      <FormItem name="releaseDate" label="Release Date">
                        <TextField
                          id="SuperSearchReleaseDate"
                          placeholder="MM/DD/YYYY"
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={8}>
                      <FormItem name="bookingNumber" label="Booking Number">
                        <TextField id="SuperSearchBookingNumber" />
                      </FormItem>
                    </StyledGridCol>
                  </SearchFormRow>
                )}
              </>
            )}

            <SearchFormRow gutter={16} align="bottom">
              <StyledGridCol span={6}>
                <FormItem name="module" label="Module">
                  <Select
                    options={MODULE_OPTIONS}
                    placeholder="Module (Jackets if blank)"
                    allowClear
                  />
                </FormItem>
              </StyledGridCol>
            </SearchFormRow>

            <SearchFormRow gutter={8}>
              <StyledGridCol>
                <Button
                  color="primary"
                  type="primary"
                  variant="solid"
                  htmlType="submit"
                  loading={isLoading}
                >
                  {labels.search}
                </Button>
              </StyledGridCol>
              <StyledGridCol>
                <Button onClick={handleClearForm}>Clear</Button>
              </StyledGridCol>
            </SearchFormRow>
          </Form>

          {hasSearched && (
            <ResultsHint>
              {results.total} {activeMeta.plural} found. Click a row to view its
              summary.
            </ResultsHint>
          )}

          <DataTable
            width="100%"
            className="table-striped"
            selectionColumnVisible={false}
            rowUniqueId={activeMeta.rowUniqueId}
            headers={activeMeta.headers}
            data={results.rows}
            totalRecords={results.total}
            isAPiCallLoading={isLoading}
            onRowClick={handleRowClick}
            pagination={pagination}
            setPagination={setPagination}
            noDataMessage={
              hasSearched
                ? `No ${activeMeta.plural} were found for that name.`
                : "Enter a name and click Search."
            }
          />
        </>
      )}

      {step === "summary" && (
        <SummaryComponent
          row={selectedRow}
          onBack={handleBackToResults}
          onClose={handleHide}
        />
      )}
    </Modal>
  );
};

export default SuperSearchModal;
